import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, IconButton, MenuItem, Popover, Select, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTranslation } from 'react-i18next';

import { dataSources, sourceArgs } from './dataSources';
import { useWidgetData } from './useWidgetData';
import { QUICK_ACTIONS, availableQuickActions, DEFAULT_QUICK_ACTIONS } from './widgets/general';

function Segmented({ options, value, onChange, render }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {options.map((option) => {
        const active = String(option) === String(value);
        return (
          <Box
            key={String(option)}
            component="button"
            type="button"
            onClick={() => onChange(option)}
            sx={{ px: 1.25, py: 0.6, borderRadius: '8px', border: `1px solid ${active ? '#3182ce' : '#e2e8f0'}`, bgcolor: active ? 'rgba(49,130,206,0.10)' : '#fff', color: active ? '#1a365d' : '#64748b', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}
          >
            {render ? render(option) : option}
          </Box>
        );
      })}
    </Box>
  );
}

function EmployeeField({ value, onChange }) {
  const { t } = useTranslation();
  const [key, fetch, options] = sourceArgs(dataSources.employees());
  const { data } = useWidgetData(key, fetch, options);
  const employees = Array.isArray(data) ? data : [];
  return (
    <Select size="small" fullWidth value={value ?? ''} onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)} displayEmpty sx={{ borderRadius: '10px' }}>
      <MenuItem value="">{t('dashboard.settings.allEmployees')}</MenuItem>
      {employees.map((employee) => (
        <MenuItem key={employee.id} value={employee.id}>
          {employee.name}
        </MenuItem>
      ))}
    </Select>
  );
}

function ActionsField({ value, onChange, organization }) {
  const { t } = useTranslation();
  const available = availableQuickActions(organization);
  const selected = (Array.isArray(value) && value.length ? value : DEFAULT_QUICK_ACTIONS).filter((key) => available.includes(key));
  const ordered = [...selected, ...available.filter((key) => !selected.includes(key))];
  const toggle = (key) => onChange(selected.includes(key) ? selected.filter((item) => item !== key) : [...selected, key]);
  const move = (key, delta) => {
    const index = selected.indexOf(key);
    const next = index + delta;
    if (index === -1 || next < 0 || next >= selected.length) return;
    const copy = selected.slice();
    copy.splice(index, 1);
    copy.splice(next, 0, key);
    onChange(copy);
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {ordered.map((key) => {
        const checked = selected.includes(key);
        return (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Checkbox size="small" checked={checked} onChange={() => toggle(key)} />
            <Typography sx={{ fontSize: 13, flex: 1 }}>{t(QUICK_ACTIONS[key].labelKey)}</Typography>
            <IconButton size="small" disabled={!checked || selected.indexOf(key) === 0} onClick={() => move(key, -1)}>
              <KeyboardArrowUpIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" disabled={!checked || selected.indexOf(key) === selected.length - 1} onClick={() => move(key, 1)}>
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      })}
      <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5 }}>{t('dashboard.settings.firstIsPrimary')}</Typography>
    </Box>
  );
}

/** Design screen 9: per-widget options with their own Peruuta / Tallenna. */
export default function WidgetSettingsPopover({ anchorEl, entry, opts, organization, onClose, onSave }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(opts || {});
  useEffect(() => setDraft(opts || {}), [opts, anchorEl]);
  const open = Boolean(anchorEl) && Boolean(entry);
  if (!entry) return null;
  const set = (key, value) => setDraft((previous) => ({ ...previous, [key]: value }));

  const renderField = (field) => {
    const value = draft[field.key];
    switch (field.kind) {
      case 'count':
        return <Segmented options={field.options} value={value ?? field.options[1]} onChange={(option) => set(field.key, option)} />;
      case 'employee':
        return <EmployeeField value={value} onChange={(option) => set(field.key, option)} />;
      case 'period':
        return <Segmented options={field.options} value={value ?? field.options[0]} onChange={(option) => set(field.key, option)} render={(option) => t(`dashboard.chips.period.${option}`, { defaultValue: option })} />;
      case 'dayOffset':
        return <Segmented options={field.options} value={value ?? 0} onChange={(option) => set(field.key, option)} render={(option) => t(`dashboard.settings.dayOffsetOptions.${option}`)} />;
      case 'ageLimit':
        return <Segmented options={field.options} value={value ?? 14} onChange={(option) => set(field.key, option)} render={(option) => `${option} ${t('dashboard.chips.daysShort')}`} />;
      case 'responseDays':
        return <Segmented options={field.options} value={value ?? 7} onChange={(option) => set(field.key, option)} render={(option) => `${option} ${t('dashboard.chips.daysShort')}`} />;
      case 'align':
        return <Segmented options={field.options} value={value ?? 'right'} onChange={(option) => set(field.key, option)} render={(option) => t(`dashboard.settings.alignOptions.${option}`)} />;
      case 'actions':
        return <ActionsField value={value} onChange={(option) => set(field.key, option)} organization={organization} />;
      default:
        return null;
    }
  };

  return (
    <Popover open={open} anchorEl={anchorEl} onClose={onClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} PaperProps={{ sx: { width: 320, borderRadius: '12px', p: 2 }, 'data-testid': 'widget-settings-popover' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <SettingsIcon sx={{ fontSize: 18, color: '#64748b' }} />
        <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{t(`dashboard.widgets.${entry.id}.title`)}</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {(entry.settings || []).map((field) => (
          <Box key={field.key}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, mb: 0.5 }}>{t(`dashboard.settings.${field.key}`)}</Typography>
            {renderField(field)}
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button size="small" variant="outlined" onClick={onClose}>
          {t('dashboard.edit.cancel')}
        </Button>
        <Button size="small" variant="contained" color="secondary" data-testid="widget-settings-save" onClick={() => onSave(draft)}>
          {t('dashboard.edit.save')}
        </Button>
      </Box>
    </Popover>
  );
}
